/**
 * Created by shanie on 11/3/16.
 */

'use strict';

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';

// Components
import RequestShippingQuoteModal from 'dibs-request-shipping-modal';
import CheckMarkIcon from 'dibs-vg/dist/react/checkmark';
import IconButton from '../Toolbar/IconButton';
import RemoveIcon from 'dibs-vg/dist/react/trash-can';
import ShareIcon from 'dibs-vg/dist/react/share';
import ShippingIcon from 'dibs-vg/dist/react/shipping';
import OnHoldIcon from 'dibs-vg/dist/react/on-hold';
import { Button } from 'dibs-elements/exports/Button';
import Tooltip from '../Tooltip';
import { BulkActionsFolderRenderer } from './BulkActionsFolderRenderer';

// Helpers
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import { copy, cutoffs, modals, mutations, tooltips } from '../../constants';
import trackFavoritesEvent from '../../helpers/trackFavoritesEvent';
import bulkHoldRequestLauncher from 'dibs-buyer-layout/exports/bulkHoldRequestLauncher';

// Styles
import styles from './styles/BulkActions.scss';

// Tracking
import { trackEvent } from 'dibs-tracking';

class BulkActions extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            hoveringShare: false,
            isBulkRequestFinished: false,
            isQuoteShippingModalOpen: false,
        };

        this.toggleSelectAll = this.toggleSelectAll.bind(this);
        this.toggleSelectingMode = this.toggleSelectingMode.bind(this);
        this.onAddItem = this.onAddItem.bind(this);
        this.onShare = this.onShare.bind(this);

        this.openQuoteShippingModal = this.openQuoteShippingModal.bind(this);
        this.onQuoteShippingModalClose = this.onQuoteShippingModalClose.bind(this);
        this.onBulkRequestModalClose = this.onBulkRequestModalClose.bind(this);
        this.onBulkActionSuccess = this.onBulkActionSuccess.bind(this);

        this.openHoldRequestModal = this.openHoldRequestModal.bind(this);
    }

    toggleSelectAll() {
        if (this.props.selectAllStatus === 'select') {
            this.props.setSelectMode(false);
            this.props.setSelectAll('deselect');
        } else {
            this.props.setSelectAll('select');
        }
    }

    toggleSelectingMode() {
        this.props.resetUiState({ retainItems: true });
        this.props.setSelectMode(!this.props.selecting);
    }

    onAddItem(response, type) {
        const action = type === modals.ADD_ITEMS ? mutations.types.ADD : mutations.types.REMOVE;
        trackFavoritesEvent({ action });
        this.props.setFullPageLoading(false);
        this.props.setOpenModalType(type);
        this.props.setShowErrorSuccessModal(true);
        this.props.setMutationResponse(response);
        this.props.selectItems([]);
        this.props.setSelectAll('');
        this.props.setSelectMode(false);
        this.props.setModalOpen(true);
    }

    onShare() {
        trackEvent({
            action: 'email a friend initiated',
            label: this.props.selectedItems.length > 1 ? 'multiple items' : 'item',
            category: 'email a friend',
        });

        this.props.shareItems();
    }

    onBulkRequestModalClose() {
        const { isBulkRequestFinished } = this.state;
        if (isBulkRequestFinished) {
            this.toggleSelectingMode();
        }

        this.setState({ isBulkRequestFinished: false });
    }

    openQuoteShippingModal() {
        this.setState({ isQuoteShippingModalOpen: true, isBulkRequestFinished: false });

        trackEvent({
            action: 'shipping quote clicked',
            label: 'favorites - bulk actions',
            category: 'contact dealer',
        });
    }

    onQuoteShippingModalClose() {
        this.setState({ isQuoteShippingModalOpen: false }, this.onBulkRequestModalClose);
    }

    onBulkActionSuccess(actionType, itemsSucceeded = []) {
        trackEvent({
            action: `${actionType} submitted`,
            label: `favorites - bulk actions|${itemsSucceeded.length}`,
            category: 'contact dealer',
        });

        this.setState({ isBulkRequestFinished: true });
    }

    openHoldRequestModal() {
        const { relay, userId, selectedItemIds: itemIds } = this.props;

        trackEvent({
            action: 'hold request clicked',
            label: 'favorites - bulk actions',
            category: 'contact dealer',
        });

        bulkHoldRequestLauncher({
            environment: relay.environment,
            userId,
            itemIds,
            onSuccess: ({ itemsSucceeded }) =>
                this.onBulkActionSuccess('hold request', itemsSucceeded),
        });
    }

    render() {
        const { isQuoteShippingModalOpen } = this.state;
        const {
            viewer,
            isTablet,
            portfolioId,
            removeItems,
            selectAllStatus,
            selectedItems,
            selectedItemIds,
            userId,
        } = this.props;

        const selectedItemImages = selectedItems.map(item => item.src);
        const selectedItemTitles = selectedItems.map(item => item.title);
        const markerClasses = classnames(styles.marker, {
            [styles.selectedAll]: selectAllStatus === 'select',
        });

        const selectAllIcon = () => (
            <div className={markerClasses}>
                <CheckMarkIcon className={styles.checkIcon} />
            </div>
        );
        const isVerifiedTrade = get(viewer, 'user.isVerifiedTrade');

        const shareButton = (
            <div
                className={styles.tooltip}
                onMouseEnter={() => this.setState({ hoveringShare: true })}
                onMouseLeave={() => this.setState({ hoveringShare: false })}
            >
                <IconButton
                    dataTn="share-items"
                    IconComponent={ShareIcon}
                    isTablet={isTablet}
                    buttonText="share"
                    disabled={
                        !selectedItems.length || selectedItems.length > cutoffs.MAX_SHARE_ITEMS
                    }
                    onClick={this.onShare}
                    upperCase={false}
                />
                <Tooltip
                    copy={copy.tooltips.SHARE_ITEMS}
                    hovering={this.state.hoveringShare}
                    showOnHover={
                        selectedItems.length > cutoffs.MAX_SHARE_ITEMS && !this.props.sticky
                    }
                    type={tooltips.SHARE_ITEMS}
                />
            </div>
        );

        const deleteButton = (
            <IconButton
                dataTn="delete-items"
                IconComponent={RemoveIcon}
                isTablet={isTablet}
                buttonText="delete"
                disabled={!selectedItems.length}
                onClick={removeItems}
                upperCase={false}
            />
        );

        const copyToFolderButton = (
            <div className={!isTablet ? styles.folderIcon : null}>
                <BulkActionsFolderRenderer
                    environment={this.props.relay.environment}
                    allowSavingMultipleItems
                    authModalTrackingLabel="save item - favorites page"
                    currentPortfolioId={portfolioId || null}
                    disabledButton={!selectedItems.length}
                    iconPosition="relative"
                    iconText={!isTablet ? 'Copy' : null}
                    loadItemData={false}
                    loadPortfolioData={!!selectedItemIds.length}
                    modalHeaderText="Copy to Folder"
                    onAddItemSuccess={() =>
                        this.onAddItem(mutations.responses.SUCCESS, modals.ADD_ITEMS)
                    }
                    onAddItemError={() =>
                        this.onAddItem(mutations.responses.ERROR, modals.ADD_ITEMS)
                    }
                    onRemoveItemError={() =>
                        this.onAddItem(mutations.responses.ERROR, modals.REMOVE_ITEMS)
                    }
                    onRemoveItemSuccess={() =>
                        this.onAddItem(mutations.responses.SUCCESS, modals.REMOVE_ITEMS)
                    }
                    onFolderRemove={() => {
                        this.props.setFullPageLoading(true);
                        this.props.setMutationResponse('');
                    }}
                    onFolderSave={() => {
                        this.props.setFullPageLoading(true);
                        this.props.setMutationResponse('');
                    }}
                    onNewFolderSave={() => {
                        this.props.setFullPageLoading(true);
                        this.props.setMutationResponse('');
                    }}
                    primaryItemPk={
                        selectedItemIds && selectedItemIds.length === 1 ? selectedItemIds[0] : ''
                    }
                    singlePortfolioItem={selectedItemIds && selectedItemIds.length === 1}
                    selectedItemIds={selectedItemIds}
                    selectedItemImages={selectedItemImages}
                    selectedItemTitles={selectedItemTitles}
                    shouldFillInFolder={!!userId}
                    userId={userId}
                    userIds={[userId]}
                    viewer={null}
                    viewerId={viewer.id}
                />
            </div>
        );

        const containerClasses = classnames(styles.container, {
            [styles.border]: !this.props.sticky,
        });

        return (
            <div className={containerClasses}>
                <div className={styles.select}>
                    <Button
                        size="medium"
                        dataTn="cancel-button"
                        onClick={this.toggleSelectingMode}
                        type="secondary"
                    >
                        cancel
                    </Button>
                    <div className={styles.selectButton}>
                        <IconButton
                            additionalIconClass={styles.folderIcon}
                            buttonText={
                                selectAllStatus === 'select'
                                    ? 'Deselect All Items'
                                    : 'Select All Items'
                            }
                            customClass="favorites"
                            dataTn="select-all-button"
                            IconComponent={selectAllIcon}
                            onClick={this.toggleSelectAll}
                            style="secondary"
                            upperCase={false}
                        />
                    </div>
                </div>
                <div className={styles.bulkActions}>
                    {!isVerifiedTrade && (
                        <Fragment>
                            {shareButton}
                            {copyToFolderButton}
                            {deleteButton}
                        </Fragment>
                    )}

                    {isVerifiedTrade && (
                        <Fragment>
                            <IconButton
                                dataTn="request-items-shipping-quotes"
                                IconComponent={ShippingIcon}
                                isTablet={isTablet}
                                buttonText="Quote Shipping"
                                onClick={this.openQuoteShippingModal}
                                upperCase={false}
                            />
                            <IconButton
                                dataTn="request-items-hold"
                                IconComponent={OnHoldIcon}
                                isTablet={isTablet}
                                buttonText="Request Holds"
                                onClick={this.openHoldRequestModal}
                                upperCase={false}
                            />
                            {copyToFolderButton}
                            {shareButton}
                            {deleteButton}
                        </Fragment>
                    )}

                    <RequestShippingQuoteModal
                        viewer={viewer}
                        isOpened={isQuoteShippingModalOpen}
                        closeShippingModal={this.onQuoteShippingModalClose}
                        onSuccess={({ itemsSucceeded }) =>
                            this.onBulkActionSuccess('shipping quote', itemsSucceeded)
                        }
                        userId={userId}
                        user={null}
                        bulkItemIds={selectedItemIds}
                        loadToStep="quoteRequest"
                    />
                </div>
            </div>
        );
    }
}

BulkActions.propTypes = {
    relay: PropTypes.object.isRequired,
    isTablet: PropTypes.bool,
    portfolioId: PropTypes.string,
    removeItems: PropTypes.func,
    resetUiState: PropTypes.func,
    setFullPageLoading: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    shareItems: PropTypes.func,
    selectItems: PropTypes.func,
    selectAllStatus: PropTypes.string,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    setOpenModalType: PropTypes.func,
    setSelectAll: PropTypes.func,
    setSelectMode: PropTypes.func.isRequired,
    setShowErrorSuccessModal: PropTypes.func,
    sticky: PropTypes.bool,
    tab: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        selectAllStatus: state.favorites.uiState.selectAllStatus,
        selectedItems: state.favorites.uiState.selectedItems,
        selecting: state.favorites.uiState.selecting,
        sortBySelection: state.favorites.uiState.sortBySelection,
        tab: state.favorites.uiState.tab,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: ({ retainItems = false, retainSort = false, retainSearch = false } = {}) => {
            dispatch(actions.resetUiState({ retainItems, retainSort, retainSearch }));
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
        setSelectAll: selectAllStatus => {
            dispatch(actions.setSelectAll({ selectAllStatus }));
        },
        setSelectMode: selecting => {
            dispatch(actions.setSelectMode({ selecting }));
        },
        setShowErrorSuccessModal: showErrorSuccessModal => {
            dispatch(actions.setShowErrorSuccessModal({ showErrorSuccessModal }));
        },
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(BulkActions);
